import React, { useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { ELD_DRIVER_STATUS, statusToString } from 'src/constants/ELDConstants';

import './hosEventListItem.scss';
import { millisecondsToHours, formatStartTime } from 'src/utils/dateUtils';
import { GeoLocationDataType, HosGridObject } from 'src/features/hosLogs/hosLog/types';
import { getHosLog } from 'src/features/hosLogs/store';
import { round } from 'src/features/hosLogs/hosEditLog/utils';
import { HOURS_IN_DAY, isNoonToNoonLog } from '../LogView/utils';

interface Props {
  logData: any;
  hosGridObject: HosGridObject;
  index: number;
  nextHosGridObject: HosGridObject;
  onEdit: (hosGridObject: HosGridObject) => void;
  isCurrentStatus: boolean;
  logDate: string;
  timeZone: string;
  allowOdometerEdits: boolean;
}

export const styleByStatus = {
  [ELD_DRIVER_STATUS.EMPTY]: '',
  [ELD_DRIVER_STATUS.OFF]: 'log-info-header__off',
  [ELD_DRIVER_STATUS.SB]: 'log-info-header__sb',
  [ELD_DRIVER_STATUS.D]: 'log-info-header__d',
  [ELD_DRIVER_STATUS.ON]: 'log-info-header__on',
  [ELD_DRIVER_STATUS.OFF_PC]: 'log-info-header__off',
  [ELD_DRIVER_STATUS.ON_YM]: 'log-info-header__on',
};

export const HosEventListItem: React.FC<Props> = ({
  logData,
  hosGridObject,
  index,
  isCurrentStatus,
  onEdit,
  nextHosGridObject,
  logDate,
  timeZone,
  allowOdometerEdits,
}) => {
  const {
    status,
    time,
    notes,
    location,
    geoLocationData,
    pcymStatus,
    duration,
    odometer,
    editedOdometer,
  } = hosGridObject;
  const tz = hosGridObject?.time.split(' ')[1];
  const isNoonToNoon = isNoonToNoonLog(logData);

  const finalHour = isNoonToNoon
    ? moment.tz(logData.logEndTimeISO, timeZone).hour() - 1
    : HOURS_IN_DAY - 1;
  let nextTime = nextHosGridObject?.time || `${finalHour}:59:00.000 ${tz}`;

  const odometerValue = odometer
    ? round(odometer) + ' mi'
    : editedOdometer
    ? round(editedOdometer) + ' mi'
    : '--';

  const isSame = isNoonToNoon
    ? moment().isBetween(moment(logData.logStartTimeISO), moment(logData.logEndTimeISO))
    : moment(logDate).isSame(moment().tz(timeZone), 'day');
  if (!nextHosGridObject?.time && isSame) {
    nextTime = '';
  }

  const headerStyle = classnames('log-info-header', styleByStatus[status as ELD_DRIVER_STATUS]);
  const tableRowStyle = classnames('log-info-box', { 'is-desktop': !isMobile });
  const showLocation = (
    driverLocation: string,
    geoLocationData?: GeoLocationDataType | null,
  ): string => {
    const { distance_mi, direction, city, jurisdiction, lat, lng } = geoLocationData || {};
    if (distance_mi && direction && jurisdiction && city) {
      return `${distance_mi.toFixed(1)} mi ${direction} ${city}, ${jurisdiction}`;
    } else if (lat && lng) {
      return `${lat}, ${lng}`;
    } else {
      return driverLocation ? driverLocation : '--';
    }
  };
  const { exemptStatus = false, certifiedStatus = 0 } = useSelector(getHosLog);

  const durationLabel = useMemo(() => {
    return millisecondsToHours(duration);
  }, [duration]);

  const timeLabel = () => {
    const timeValue = time ? formatStartTime(time) : '--';
    const nextTimeValue = time && nextTime ? formatStartTime(nextTime) : '--';

    return (
      <>
        {timeValue}
        {nextTime && (
          <>
            &nbsp;-&nbsp;<span className="log-info-box_time">{nextTimeValue}</span>
          </>
        )}
      </>
    );
  };

  const handleEditStatus = useCallback(() => {
    onEdit({ ...hosGridObject, index: index });
  }, [hosGridObject, onEdit]);

  const isMobileEditing = isMobile;
  const isDesktopEditing = !isMobile;

  const renderMobileEdit = (): JSX.Element | null => {
    return status === ELD_DRIVER_STATUS.D || isCurrentStatus ? null : (
      <>
        {/* Temporarily Disabled */ false && isMobileEditing && !exemptStatus && !certifiedStatus && (
          <div className="log-info-header__edit-status" onClick={handleEditStatus}>
            Edit Status
          </div>
        )}
      </>
    );
  };

  const renderDesktopEdit = (): JSX.Element => {
    return (
      <>
        {/* Temporarily Disabled */ false && isDesktopEditing && (
          <td data-title="Edit Status">
            {status === ELD_DRIVER_STATUS.D ||
            isCurrentStatus ||
            exemptStatus ||
            certifiedStatus ? null : (
              <div className="log-info-header__edit-status-desktop" onClick={handleEditStatus}>
                Edit Status
              </div>
            )}
          </td>
        )}
      </>
    );
  };

  return (
    <>
      <tr className={tableRowStyle} key={time}>
        <td className={headerStyle}>
          {statusToString(status)}
          {renderMobileEdit()}
        </td>
        <td data-title="Time">{timeLabel()}</td>
        <td data-title="Duration">{durationLabel} HRS</td>
        <td data-title="Location">{showLocation(location, geoLocationData)}</td>
        <td data-title="PC/YM">{pcymStatus ? pcymStatus : '--'}</td>
        <td data-title="Note">{notes ? notes : '--'}</td>
        {allowOdometerEdits && <td data-title="Odometer">{odometerValue}</td>}
        <td className="no-padding" />
        {renderDesktopEdit()}
      </tr>
    </>
  );
};
